import React, { useEffect, useState } from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { getAdImageApi, PREVIEW_FORMATS, previewAdApi, type CampaignAd, type PreviewFormat } from "../campanhasApi";
import AdPreviewMock from "./AdPreviewMock";

type Props = {
  campaignId: string;
  ad: CampaignAd;
  /** Integração com a Meta ligada no servidor: sem ela, só a simulada. */
  metaEnabled: boolean;
  onClose: () => void;
};

/**
 * Prévia de um anúncio salvo. A simulada aparece aqui; a OFICIAL, gerada pela
 * Meta (igual ao que vai ao ar, sem criar nada na conta), abre numa aba nova:
 * a Meta recusa mostrar a prévia dentro de outro site (iframe) e só a monta para
 * quem está logado no Facebook — conferido em 31/10/2026.
 */
const AdPreviewModal: React.FC<Props> = ({ campaignId, ad, metaEnabled, onClose }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [opening, setOpening] = useState<PreviewFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A imagem gravada, para a simulada.
  useEffect(() => {
    let url: string | null = null;
    getAdImageApi(campaignId, ad.id)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      })
      .catch(() => setImageUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [campaignId, ad.id]);

  // A aba abre no clique (senão o navegador bloqueia como pop-up) e recebe o endereço quando a Meta responde.
  const openOfficial = async (format: PreviewFormat) => {
    setError(null);
    const tab = window.open("", "_blank");
    if (tab) {
      tab.opener = null;
      tab.document.title = "Gerando a prévia na Meta...";
      tab.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px">Gerando a prévia na Meta...</p>';
    }
    try {
      setOpening(format);
      const preview = await previewAdApi(campaignId, ad.id, format);
      if (tab) tab.location.replace(preview.src);
      else setError("O navegador bloqueou a aba nova. Libere os pop-ups deste site e tente de novo.");
    } catch (e: any) {
      tab?.close();
      setError(e?.message || "Não foi possível gerar a prévia.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="ecoDialog__overlay">
      <div className="cmpModal cmpModal--preview" role="dialog" aria-modal="true" aria-labelledby="cmp-preview-title">
        <div className="cmpModal__head">
          <div>
            <h2 id="cmp-preview-title" className="cmpModal__title">
              Prévia do anúncio {ad.position}
            </h2>
            <p className="cmpHint">{ad.headline}</p>
          </div>
          <button type="button" className="ecoBtn ecoBtn--icon" aria-label="Fechar" data-modal-close onClick={onClose}>
            <EcoIcon name="x" size={18} />
          </button>
        </div>

        <AdPreviewMock primaryText={ad.primaryText} headline={ad.headline} description={ad.description ?? ""} imageUrl={imageUrl} />

        <section className="cmpOfficialLinks" aria-label="Prévia oficial da Meta">
          <h3 className="cmpPanel__title">Prévia oficial da Meta</h3>
          {metaEnabled ? (
            <>
              <p className="cmpHint">
                Exatamente como a Meta vai mostrar, sem criar nada na conta. Abre numa aba nova e precisa que você esteja logado no Facebook com uma
                conta que tenha acesso à conta de anúncios da empresa.
              </p>
              <div className="cmpOfficialLinks__grid">
                {PREVIEW_FORMATS.map((f) => (
                  <button key={f.value} type="button" className="ecoBtn" onClick={() => openOfficial(f.value)} disabled={opening !== null}>
                    <EcoIcon name="arrowUpRight" size={16} />
                    {opening === f.value ? "Gerando..." : f.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="cmpHint">Precisa da integração com a Meta ligada no servidor. Por enquanto, só a simulada.</p>
          )}
          {error ? (
            <p className="cmpNotice cmpNotice--bad">
              <EcoIcon name="alertTriangle" size={16} />
              <span>{error}</span>
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default AdPreviewModal;
